import React, { useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Check } from 'lucide-react';
import type { UnitBadgeSeverity, DeltaDocument } from '@platform/shared-types';
import { UNIT_BADGES, worstBadgeSeverity } from './unitBadgeSafe';

/**
 * THE NOTE AND BADGES ON ONE DEVICE UNIT.
 *
 * Two different kinds of statement, deliberately in one place. A BADGE is a
 * fixed, filterable fact — "received good", "screen fault" — that belongs in the
 * device's history with a timestamp. A NOTE is the sentence a badge cannot
 * carry: which corner is scratched, what the vendor said, what to check next
 * time.
 *
 * DELTA, NOT HTML. Quill can hand back either; this stores the Delta, because
 * the app ships no sanitiser and rendering operator-authored markup would be a
 * stored-XSS path — narrow, since author and reader both hold admin-pool
 * tokens, but an escalation route inside an admin tool all the same. A Delta is
 * data. The plain text is derived alongside it and remains what every existing
 * consumer reads, so nothing else has to learn what a Delta is.
 */

/** Enough room to write a paragraph without the box fighting you — ~10 lines. */
const MIN_EDITOR_PX = 210;

const SEVERITY_CLASS: Record<UnitBadgeSeverity, { on: string; off: string }> = {
  good: {
    on: 'border-emerald-600 bg-emerald-600 text-white',
    off: 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-400',
  },
  watch: {
    on: 'border-amber-500 bg-amber-500 text-white',
    off: 'border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-400',
  },
  fault: {
    on: 'border-[#DC143C] bg-[#DC143C] text-white',
    off: 'border-red-200 bg-[#FFF1F3] text-[#9F1239] hover:border-[#DC143C]',
  },
};

/** The toolbar an operator actually needs: emphasis, structure, a link. */
const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'align', 'link'];

/** An empty Delta is `[{ insert: '\n' }]` — never `[]`, which Quill rejects. */
export const EMPTY_DELTA: DeltaDocument = { ops: [{ insert: '\n' }] } as DeltaDocument;

/**
 * The plain text behind a Delta.
 *
 * Kept in step with `notes` on every save so list rows, exports and the note
 * pill keep working on a plain string. Line attributes ride on the terminating
 * newline, so concatenating the string inserts is the whole text.
 */
export function deltaToPlainText(delta: DeltaDocument | undefined): string {
  if (!delta?.ops) return '';
  return delta.ops
    .map((op: any) => (typeof op?.insert === 'string' ? op.insert : ''))
    .join('')
    .trim();
}

export interface UnitNotesValue {
  notes: string;
  notesRich: DeltaDocument;
  badges: string[];
}

const UnitNotesEditor: React.FC<{
  value: UnitNotesValue;
  onChange: (next: UnitNotesValue) => void;
  /** Compact drops the section heading — for a card that already has one. */
  compact?: boolean;
  disabled?: boolean;
}> = ({ value, onChange, compact, disabled }) => {
  const quillRef = useRef<any>(null);
  const [focused, setFocused] = useState(false);

  const worst = useMemo(() => worstBadgeSeverity(value.badges), [value.badges]);

  const toggleBadge = (id: string) => {
    const on = value.badges.includes(id);
    const badges = on ? value.badges.filter((b) => b !== id) : [...value.badges, id];
    onChange({ ...value, badges });
  };

  /**
   * Quill hands back its own Delta instance; the editor's text comes from the
   * same source so the two can never disagree about what was typed.
   */
  const handleChange = (_html: string, _delta: unknown, _src: unknown, editor: any) => {
    const contents = editor.getContents();
    const rich = { ops: contents?.ops || EMPTY_DELTA.ops } as DeltaDocument;
    onChange({ ...value, notesRich: rich, notes: deltaToPlainText(rich) });
  };

  return (
    <div>
      {!compact && (
        <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-stone-500">
          Condition &amp; notes
        </h4>
      )}

      <div className="mb-2">
        <span className="mb-1.5 block text-[9.5px] font-bold uppercase tracking-wide text-stone-400">
          Badges
          {worst && (
            <span className={`ml-2 rounded-full px-1.5 py-px text-[9px] ${
              worst === 'fault' ? 'bg-[#FFF1F3] text-[#9F1239]'
                : worst === 'watch' ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
            }`}>
              {worst === 'fault' ? 'Fault' : worst === 'watch' ? 'Needs attention' : 'Good'}
            </span>
          )}
        </span>
        <div className="flex flex-wrap gap-1.5">
          {UNIT_BADGES.map((b) => {
            const on = value.badges.includes(b.id);
            const cls = SEVERITY_CLASS[b.severity];
            return (
              <button
                key={b.id}
                type="button"
                disabled={disabled}
                aria-pressed={on}
                onClick={() => toggleBadge(b.id)}
                className={`inline-flex min-h-[32px] items-center gap-1 rounded-full border px-2.5 text-[11.5px] font-semibold transition-colors disabled:opacity-50 ${
                  on ? cls.on : cls.off
                }`}
              >
                {on && <Check size={11} />}
                {b.label}
              </button>
            );
          })}
        </div>
        {/* Every toggle becomes a timestamped row in the device's own history,
            which is what makes "did it come back good?" answerable later. */}
        <p className="mt-1.5 text-[10.5px] text-stone-500">
          Each change is stamped into this device's history when you save.
        </p>
      </div>

      <div>
        <span className="mb-1.5 block text-[9.5px] font-bold uppercase tracking-wide text-stone-400">
          Note / special instruction
        </span>
        <div
          className={`overflow-hidden rounded-lg border bg-white ${
            focused ? 'border-[#DC143C]' : 'border-stone-300'
          }`}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            readOnly={disabled}
            value={value.notesRich as any}
            onChange={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="What arrived, what to watch for, what the vendor said…"
          />
        </div>
      </div>

      {/* Quill ships its own chrome; these align it to the surrounding card
          rather than leaving a 1996 grey bar in the middle of the page. */}
      <style>{`
        .ql-toolbar.ql-snow { border: 0; border-bottom: 1px solid #E7E5E4; background: #FAFAF9; padding: 5px 7px; }
        .ql-container.ql-snow { border: 0; font-family: inherit; font-size: 13px; }
        .ql-editor { min-height: ${MIN_EDITOR_PX}px; }
        .ql-editor.ql-blank::before { color: #A8A29E; font-style: normal; }
        .ql-snow .ql-stroke { stroke: #57534E; }
        .ql-snow .ql-fill { fill: #57534E; }
        .ql-snow .ql-picker { color: #57534E; }
        .ql-snow.ql-toolbar button:hover .ql-stroke,
        .ql-snow.ql-toolbar button.ql-active .ql-stroke { stroke: #DC143C; }
        .ql-snow.ql-toolbar button:hover .ql-fill,
        .ql-snow.ql-toolbar button.ql-active .ql-fill { fill: #DC143C; }
      `}</style>
    </div>
  );
};

export default UnitNotesEditor;
